import { FetchResult, NetworkStatus, useQuery } from "@apollo/client";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useState } from "react";

import {
  DeleteOptOutListMutation,
  GlobalOptOutSummaryQuery,
  SubaccountOptOutSummaryQuery,
  TrackedJobStatus,
  UploadAdminOptOutListMutation,
} from "~frontend/__generated__/graphql";
import {
  DELETE_OPT_OUT_LIST,
  GET_GLOBAL_OPT_OUT_SUMMARY,
  GET_SUBACCOUNT_OPT_OUT_SUMMARY,
  REFRESH_SUBACCOUNT_OPT_OUT_COUNTS,
  REFRESH_TENANT_OPT_OUT_COUNTS,
  UPLOAD_ADMIN_OPT_OUT_LIST,
} from "~frontend/graphql/optOuts";
import useMutation from "~frontend/util/useMutation";

const refetchQueries = [
  GET_SUBACCOUNT_OPT_OUT_SUMMARY,
  GET_GLOBAL_OPT_OUT_SUMMARY,
];

// TODO: As part of Opt Out page optimization (STW-447), we may break apart the summary
// endpoint, so this hook could probably be renamed to `useOptOutLists` and modified accordingly
export const useOptOutSummary = ({
  subaccountId = null,
}: {
  subaccountId?: string | null;
}): {
  loading: boolean;
  error?: Error;
  summary?:
    | GlobalOptOutSummaryQuery["globalOptOutSummary"]
    | SubaccountOptOutSummaryQuery["subaccountOptOutSummary"];
  refetch: () => Promise<
    FetchResult<GlobalOptOutSummaryQuery | SubaccountOptOutSummaryQuery>
  >;
  deleteList: (
    listId: string
  ) => Promise<FetchResult<DeleteOptOutListMutation>>;
  isListDeleting: boolean;
  createList: (args: {
    uploadMetadata: {
      estimatedRowCount: number;
      uploadToken: string;
      phoneNumberColumn: string;
    };
    values: {
      name: string;
      phoneNumberColumn: string;
      type: string;
      expiresAt: DateTime | null;
      skipAlreadyOptedOut: boolean;
    };
  }) => Promise<FetchResult<UploadAdminOptOutListMutation>>;
  refreshCounts: () => Promise<void>;
  refreshingCounts: boolean;
  refetching: boolean;
} => {
  const isGlobal = !subaccountId;

  const query = isGlobal
    ? GET_GLOBAL_OPT_OUT_SUMMARY
    : GET_SUBACCOUNT_OPT_OUT_SUMMARY;

  const summaryInput = !isGlobal ? { input: { subaccountId } } : undefined;

  const {
    loading,
    error,
    data,
    refetch,
    networkStatus,
    startPolling,
    stopPolling,
  } = useQuery<GlobalOptOutSummaryQuery | SubaccountOptOutSummaryQuery>(query, {
    variables: summaryInput,
    notifyOnNetworkStatusChange: true,
  });

  const summary = isGlobal
    ? (data as GlobalOptOutSummaryQuery)?.globalOptOutSummary
    : (data as SubaccountOptOutSummaryQuery)?.subaccountOptOutSummary;

  const [deleteMutation, { loading: isListDeleting }] =
    useMutation<DeleteOptOutListMutation>(DELETE_OPT_OUT_LIST, {
      onErrorSnackbarMessage: "Error deleting list",
      refetchQueries,
    });

  const deleteList = async (listId: string) => {
    return deleteMutation({
      variables: {
        input: {
          listId,
        },
      },
      update: (cache, { data: deletePayload }) => {
        const {
          deleteOptOutList: { id: deletingListId },
        } = deletePayload!;

        const newOptOutLists = summary.optOutLists.filter(
          list => list.id !== deletingListId
        );

        const newData = isGlobal
          ? {
              ...data,
              globalOptOutSummary: {
                ...(summary as GlobalOptOutSummaryQuery["globalOptOutSummary"]),
                optOutLists: newOptOutLists,
              },
            }
          : {
              ...data,
              subaccountOptOutSummary: {
                ...(summary as SubaccountOptOutSummaryQuery["subaccountOptOutSummary"]),
                optOutLists: newOptOutLists,
              },
            };

        cache.writeQuery<
          GlobalOptOutSummaryQuery | SubaccountOptOutSummaryQuery
        >({
          query,
          variables: summaryInput,
          data: newData,
        });
      },
    });
  };

  const jobIsInProgress = useCallback(
    (job: { status: TrackedJobStatus }) =>
      job.status === TrackedJobStatus.Created ||
      job.status === TrackedJobStatus.Queued ||
      job.status === TrackedJobStatus.Running,
    []
  );

  const inProgressDeletions = useMemo(
    () => summary?.deleteOptOutListJobs.filter(job => jobIsInProgress(job)),
    [summary, jobIsInProgress]
  );

  const inProgressExpirationEdits = useMemo(
    () =>
      summary?.optOutLists.filter(
        list =>
          list.editExpirationJob && jobIsInProgress(list.editExpirationJob)
      ),
    [summary, jobIsInProgress]
  );

  const [startImport] = useMutation(UPLOAD_ADMIN_OPT_OUT_LIST, {
    onErrorSnackbarMessage: "Error starting import",
    refetchQueries,
    update: (cache, { data: payload }) => {
      if (payload?.uploadAdminOptOutList) {
        const newData = {
          ...data,
          [`${isGlobal ? "globalOptOutSummary" : "subaccountOptOutSummary"}`]: {
            ...summary,
            optOutLists: [
              payload.uploadAdminOptOutList,
              ...summary.optOutLists,
            ],
          },
        };

        cache.writeQuery({
          query,
          variables: summaryInput,
          data: newData,
        });
      }
    },
  });

  const createList = async ({
    uploadMetadata,
    values,
  }: {
    uploadMetadata: {
      estimatedRowCount: number;
      uploadToken: string;
      phoneNumberColumn: string;
    };
    values: {
      name: string;
      phoneNumberColumn: string;
      type: string;
      expiresAt: DateTime | null;
      skipAlreadyOptedOut: boolean;
    };
  }) => {
    const expiresAt = values.type === "TEMPORARY" ? values.expiresAt : null;
    return await startImport({
      variables: {
        input: {
          ...uploadMetadata,
          subaccountId: isGlobal ? null : subaccountId,
          name: values.name,
          phoneNumberColumn: values.phoneNumberColumn,
          expiresAt,
          skipAlreadyOptedOut: values.skipAlreadyOptedOut,
        },
      },
      optimisticResponse: {
        uploadAdminOptOutList: {
          __typename: "OptOutList",
          id: "pending-id",
          subaccountId: isGlobal ? null : subaccountId,
          name: values.name,
          type: values.type,
          expiresAt,
          count: uploadMetadata.estimatedRowCount,
          createdByUser: {
            __typename: "PartialUser",
            id: "pending-user-id",
            fullName: "--",
          },
          status: "PENDING",
          createdAt: DateTime.now().toMillis(),
          importJob: {
            __typename: "TrackedJob",
            id: "pending-job-id",
            status: "CREATED",
            errorMessage: null,
            result: null,
          },
        },
      },
    });
  };

  const [refreshOptOutCounts] = useMutation(
    isGlobal
      ? REFRESH_TENANT_OPT_OUT_COUNTS
      : REFRESH_SUBACCOUNT_OPT_OUT_COUNTS,
    {
      variables: isGlobal ? undefined : { input: { subaccountId } },
      onErrorSnackbarMessage: "Error refreshing",
      refetchQueries,
      awaitRefetchQueries: true,
    }
  );

  const [submittingRefreshCounts, setSubmittingRefreshCounts] = useState(false);
  const refreshCounts = useCallback(async () => {
    setSubmittingRefreshCounts(true);
    await refreshOptOutCounts();
    setSubmittingRefreshCounts(false);
  }, [setSubmittingRefreshCounts, refreshOptOutCounts]);

  useEffect(() => {
    if (
      summary?.refreshing ||
      inProgressDeletions?.length ||
      inProgressExpirationEdits?.length
    ) {
      startPolling(1000);
    } else {
      stopPolling();
    }
    return () => stopPolling();
  }, [
    summary,
    inProgressDeletions,
    inProgressExpirationEdits,
    startPolling,
    stopPolling,
  ]);

  return {
    loading: loading && !data,
    error,
    summary,
    refetch,
    deleteList,
    isListDeleting,
    createList,
    refetching: networkStatus === NetworkStatus.refetch,
    refreshCounts,
    refreshingCounts: submittingRefreshCounts || summary?.refreshing,
  };
};
